import { useCallback, useState } from "react";

export type SortDirection = "asc" | "desc";

export function compareSortValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;

  if (typeof a === "number" && typeof b === "number") {
    return a - b;
  }

  return String(a).localeCompare(String(b), "fr", {
    sensitivity: "base",
    numeric: true,
  });
}

export function sortByColumn<T>(
  rows: T[],
  sortKey: string | null,
  sortDirection: SortDirection,
  getValue: (row: T, key: string) => unknown,
): T[] {
  if (!sortKey) return rows;

  return [...rows].sort((a, b) => {
    const result = compareSortValues(getValue(a, sortKey), getValue(b, sortKey));
    return sortDirection === "asc" ? result : -result;
  });
}

export function useTableSort(
  defaultKey: string | null = null,
  defaultDirection: SortDirection = "asc",
) {
  const [sortKey, setSortKey] = useState<string | null>(defaultKey);
  const [sortDirection, setSortDirection] = useState<SortDirection>(defaultDirection);

  const toggleSort = useCallback(
    (key: string) => {
      if (sortKey === key) {
        setSortDirection((direction) => (direction === "asc" ? "desc" : "asc"));
        return;
      }
      setSortKey(key);
      setSortDirection("asc");
    },
    [sortKey],
  );

  return { sortKey, sortDirection, toggleSort };
}
